import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { useConfirm } from '../ConfirmDialog';
import ContentDrawer from '../ContentDrawer';
import RegisterFormProvider from '../RegisterFormProvider';

const FormDrawer = ({
  title,
  isOpen,
  skipConfirm,
  onClose,
  drawerWidth,
  children,
  confirmationTitle,
  confirmationDescription,
  ...drawerProps
}) => {
  const intl = useIntl();
  const confirm = useConfirm();
  const formRef = useRef(undefined);

  /**
   * Handle closing the form drawer.
   */
  const handleCloseFormDrawer = async () => {
    const form = formRef.current ?? {};
    const { formState = {} } = form;
    const { dirty = false } = formState;

    if (dirty && !skipConfirm) {
      const confirmResponse = await confirm({
        title:
          confirmationTitle ??
          intl.formatMessage({
            id: 'confirmCancelFormDialog.title',
            defaultMessage: 'Leave window?',
            description: 'Title of the confirm cancel form dialog',
          }),
        description:
          confirmationDescription ??
          intl.formatMessage({
            id: 'confirmCancelFormDialog.description',
            defaultMessage:
              'Are you sure that you want to leave this window? You have unsaved changes',
            description: 'Description of the confirm cancel form dialog',
          }),
        catchOnCancel: false,
        buttons: [
          {
            label: intl.formatMessage({
              id: 'confirmCancelFormDialog.buttons.cancel',
              defaultMessage: 'Cancel',
              description: 'Label of the cancel button in the confirm cancel form dialog',
            }),
            type: 'cancel',
            variant: 'outlined',
            style: {
              marginRight: 'auto',
            },
          },
          {
            label: intl.formatMessage({
              id: 'confirmCancelFormDialog.buttons.leaveWindow',
              defaultMessage: 'Leave window',
              description: 'Label of the leave window button in the confirm cancel form dialog',
            }),
            response: 'discard',
            type: 'submit',
            variant: 'contained',
            color: 'primary',
          },
        ],
      });

      if (confirmResponse === 'discard' && onClose) {
        onClose(form);
      }
    } else if (onClose) {
      onClose(form);
    }
  };

  return (
    <RegisterFormProvider value={{ formRef }}>
      <ContentDrawer
        {...drawerProps}
        title={title}
        isOpen={isOpen}
        handleClose={handleCloseFormDrawer}
        drawerWidth={drawerWidth}
      >
        {children}
      </ContentDrawer>
    </RegisterFormProvider>
  );
};

FormDrawer.propTypes = {
  title: PropTypes.string,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  children: PropTypes.node,
  drawerWidth: PropTypes.string,
  skipConfirm: PropTypes.bool,
  confirmationTitle: PropTypes.string,
  confirmationDescription: PropTypes.string,
};

FormDrawer.defaultProps = {
  title: undefined,
  isOpen: false,
  onClose: undefined,
  children: undefined,
  drawerWidth: null,
  skipConfirm: false,
  confirmationTitle: undefined,
  confirmationDescription: undefined,
};

export default FormDrawer;
